<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                <div class="kgr-postfix__fluid">
                    <a
                        class="kgr-button kgr-button--link"
                        @click="redirectToOverview"
                    >
                        <kgr-icon
                            icon-class="arrow-left"
                            class="kgr-button__icon kgr-button__icon--left kgr-icon"
                        />

                        <span v-t="'backToOverview'"></span>
                    </a>
                </div>
                <div class="kgr-postfix__fixed">
                    <button
                        v-if="translateTo"
                        class="kgr-button kgr-button--accent"
                        @click="showOriginal = !showOriginal"
                    >
                        <kgr-icon
                            class="kgr-button__icon kgr-button__icon--left"
                            icon-class="globe"
                            highlight
                        />
                        {{ showOriginal ? $t('Übersetzung anzeigen') : $t('Original Text anzeigen') }}
                    </button>
                </div>
                <div
                    v-if="!loading && news.hasWriteAccess"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="editUrl"
                    >
                        <kgr-icon
                            icon-class="edit"
                            small
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('News.modifyEntry') }}</span>
                    </a>
                </div>
                <div
                    v-if="hasGeneralWriteAccess"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="createUrl"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('News.addNew') }}</span>
                    </a>
                </div>
            </div>
        </div>
        <div class="kgr-row">
            <kgr-loading v-if="loading" />
            <div
                v-if="!loading"
                class="kgr-float kgr-float--clear"
            >
                <div
                    v-if="news.image"
                    class="kgr-float kgr-float--left kgr-float--inText"
                >
                    <img
                        :src="`/backend/thumbnail/news/${news.id}/news_detail_thumb/${news.image}`"
                        alt="News Image"
                    />
                </div>
                <h3 class="kgr-headline kgr-headline--h3">
                    {{ !showOriginal && translateTo && news.translations?.title ? news.translations.title : news.title }}
                    <kgr-publication-icon
                        v-if="!hasRoleParent"
                        :clearing="news.clearing"
                    />
                </h3>
                <div class="app-meta kgr-row">
                    {{ getParsedDate(news.publishedAt) }}
                    {{ news.groups ? news.groups.map(group => group.name).join(', ') : $t('Für alle Gruppen') }}
                </div>
                <div class="app-meta kgr-row">
                    {{!showOriginal && translateTo && news.translations?.teaser ? news.translations.teaser : news.teaser }}
                </div>
                <div class="kgr-separator" style="width: auto; overflow: hidden;"></div>

                <div
                    class="kgr-wysiwyg"
                    v-html="!showOriginal && translateTo && news.translations?.text ? news.translations.text : news.text "
                >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format, parseISO} from '@legacy/js/date/date-fns.js'
import Icon from '../../components/common/icon/Icon.vue'
import Loading from '../../components/common/Loading.vue';
import NewsRepository from '../../js/repository/NewsRepository.js';
import PublicationIcon from '../../components/common/icon/PublicationIcon.vue';
import { getLanguageFromCookie } from '../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-publication-icon': PublicationIcon,
    },
    props: {
        newsId: {
            type: Number,
            required: true,
        },
        overviewUrl: {
            type: String,
            required: true,
        },
        hasGeneralWriteAccess: {
            type: Boolean,
            required: false,
            default: false
        },
        hasRoleParent: {
            type: Boolean,
            required: false,
            default: true
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            showOriginal: false,
            translateTo: null,
            news: null,
        }
    },
    computed: {
        editUrl() {
            return `/backend/news/${this.newsId}/edit`;
        },
        createUrl() {
            return '/backend/news/new';
        },
        isMissingTranslation() {
            const news = this.news;
            const hasTitle = news.title !== null && news.title.trim() !== '';
            const hasTeaser = news.teaser !== null && news.teaser.trim() !== '';
            const hasText = news.text !== null && news.text.trim() !== '';

            const missingTitle = hasTitle && !news.translations?.title;
            const missingTeaser = hasTeaser && !news.translations?.teaser;
            const missingText = hasText && !news.translations?.text;

            return missingTitle || missingTeaser || missingText;
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
            this.translate();
        } else {
            this.load();
        }
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        async translate() {
            await this.load();
            if (!this.isMissingTranslation) {
                return;
            }
            this.loading = true;
            await NewsRepository.createTranslations([this.newsId], this.translateTo);
            this.load();

        },
        getParsedDate(date) {
            const parsedDate = parseISO(date);
            const formatted = format(parsedDate, 'dd.MM.yyyy HH:mm');

            return formatted;
        },
        async load() {
            this.loading = true;
            return NewsRepository.findById(this.newsId, {lang: this.translateTo})
                .then((news) => {
                    this.news = news;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
    },
}
</script>
<style scoped>
</style>
